// SpotBidding 通常入札タブ用の mock seed。
//
// TODO(rpc): 本来は `GetBiddingSpotApprovalRequest(id)` の response から
// `spot_bids` を展開する。 list response (= BiddingSpotApprovalRequestListItem) は
// `bid_count` しか持たないため、 札一覧を画面で出すには Get N 回 or proto 拡張が必要。
// 詳細は pages/SpotBidding/models/spotBidRow.ts の TODO(proto) を参照。
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import {
  BiddingApprovalState,
  BiddingSpotBidStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

import { jstWallClockToInstant } from '~/models/dateTime'
import type {
  SpotBidIssuerType,
  SpotBidRow,
  SpotBidTranche,
} from '~/pages/SpotBidding/models/spotBidRow'

// ----- master 系 mock (= tranche) -----

export const SPOT_BID_TRANCHES_MOCK: SpotBidTranche[] = [
  { id: 'tranche-a', name: 'トランシェ A (= ベースロード)' },
  { id: 'tranche-b', name: 'トランシェ B (= ピーク)' },
  { id: 'tranche-c', name: 'トランシェ C (= 調整)' },
]

// ----- 札 1 行の seed (= 30 件) -----

const baseRequestId = 'mock-approval-request-001'
const baseCreatedAt = jstWallClockToInstant(new Date(2026, 4, 25), 9, 30) // 2026-05-25 09:30 JST

const ISSUERS: SpotBidIssuerType[] = [
  'generation',
  'retail',
  'dr',
  'selfwheeling',
]

type Seed = {
  area: AreaCode
  timeCode: number
  side: Side
  price: string
  volume: string
  status: BiddingSpotBidStatus
  issuer: SpotBidIssuerType
  trancheId?: string
  execPrice?: string
  execVolume?: string
  /** メモ（自由記述）。 社内限定。 */
  memoFree?: string
  /** メモ（JEPX提出用）。 JEPX へ note として送信。 */
  memoJepx?: string
}

const SEEDS: Seed[] = [
  // 東京エリア 朝の買 (= 通常 / FIT)
  {
    area: AreaCode.TOKYO,
    timeCode: 13,
    side: Side.BUY,
    price: '10.50',
    volume: '50.0',
    status: BiddingSpotBidStatus.MATCHED,
    issuer: 'retail',
    trancheId: 'tranche-a',
    execPrice: '10.40',
    execVolume: '50.0',
  },
  {
    area: AreaCode.TOKYO,
    timeCode: 13,
    side: Side.SELL,
    price: '9.80',
    volume: '30.0',
    status: BiddingSpotBidStatus.MATCHED,
    issuer: 'generation',
    trancheId: 'tranche-a',
    execPrice: '10.40',
    execVolume: '30.0',
  },
  {
    area: AreaCode.TOKYO,
    timeCode: 14,
    side: Side.BUY,
    price: '11.00',
    volume: '40.0',
    status: BiddingSpotBidStatus.MATCHED,
    issuer: 'retail',
    trancheId: 'tranche-a',
    execPrice: '10.80',
    execVolume: '40.0',
  },
  {
    area: AreaCode.TOKYO,
    timeCode: 14,
    side: Side.SELL,
    price: '10.20',
    volume: '25.0',
    status: BiddingSpotBidStatus.MATCHED,
    issuer: 'generation',
    trancheId: 'tranche-b',
    execPrice: '10.80',
    execVolume: '25.0',
  },
  {
    area: AreaCode.TOKYO,
    timeCode: 18,
    side: Side.BUY,
    price: '15.50',
    volume: '60.0',
    status: BiddingSpotBidStatus.PENDING,
    issuer: 'retail',
    trancheId: 'tranche-b',
  },
  // 売買逆転 (= 同 area × 同 koma で 買価格 ≥ 売価格)
  {
    area: AreaCode.TOKYO,
    timeCode: 18,
    side: Side.SELL,
    price: '14.80',
    volume: '20.0',
    status: BiddingSpotBidStatus.PENDING,
    issuer: 'generation',
    trancheId: 'tranche-b',
  },

  // 中部エリア 昼間 (= 太陽光発電を想定)
  {
    area: AreaCode.CHUBU,
    timeCode: 22,
    side: Side.SELL,
    price: '8.00',
    volume: '80.0',
    status: BiddingSpotBidStatus.MATCHED,
    issuer: 'generation',
    trancheId: 'tranche-a',
    execPrice: '8.20',
    execVolume: '80.0',
  },
  {
    area: AreaCode.CHUBU,
    timeCode: 23,
    side: Side.SELL,
    price: '7.50',
    volume: '90.0',
    status: BiddingSpotBidStatus.MATCHED,
    issuer: 'generation',
    trancheId: 'tranche-a',
    execPrice: '8.20',
    execVolume: '90.0',
  },
  {
    area: AreaCode.CHUBU,
    timeCode: 24,
    side: Side.SELL,
    price: '7.20',
    volume: '95.0',
    status: BiddingSpotBidStatus.MATCHED,
    issuer: 'generation',
    trancheId: 'tranche-a',
    execPrice: '8.20',
    execVolume: '70.0',
  },
  {
    area: AreaCode.CHUBU,
    timeCode: 25,
    side: Side.SELL,
    price: '7.80',
    volume: '85.0',
    status: BiddingSpotBidStatus.UNMATCHED,
    issuer: 'generation',
    trancheId: 'tranche-a',
  },

  // 関西エリア 夜間 (= ベースロード)
  {
    area: AreaCode.KANSAI,
    timeCode: 1,
    side: Side.BUY,
    price: '9.00',
    volume: '120.0',
    status: BiddingSpotBidStatus.MATCHED,
    issuer: 'retail',
    trancheId: 'tranche-a',
    execPrice: '8.90',
    execVolume: '120.0',
  },
  {
    area: AreaCode.KANSAI,
    timeCode: 1,
    side: Side.SELL,
    price: '8.50',
    volume: '100.0',
    status: BiddingSpotBidStatus.MATCHED,
    issuer: 'generation',
    trancheId: 'tranche-a',
    execPrice: '8.90',
    execVolume: '100.0',
  },
  {
    area: AreaCode.KANSAI,
    timeCode: 2,
    side: Side.SELL,
    price: '8.20',
    volume: '110.0',
    status: BiddingSpotBidStatus.MATCHED,
    issuer: 'generation',
    trancheId: 'tranche-a',
    execPrice: '8.70',
    execVolume: '110.0',
  },

  // 九州エリア 朝
  {
    area: AreaCode.KYUSHU,
    timeCode: 14,
    side: Side.BUY,
    price: '11.50',
    volume: '35.0',
    status: BiddingSpotBidStatus.PENDING,
    issuer: 'dr',
    trancheId: 'tranche-c',
  },
  {
    area: AreaCode.KYUSHU,
    timeCode: 15,
    side: Side.SELL,
    price: '10.80',
    volume: '40.0',
    status: BiddingSpotBidStatus.PENDING,
    issuer: 'generation',
    trancheId: 'tranche-b',
  },

  // 北海道エリア 自己託送
  {
    area: AreaCode.HOKKAIDO,
    timeCode: 20,
    side: Side.BUY,
    price: '12.00',
    volume: '15.0',
    status: BiddingSpotBidStatus.MATCHED,
    issuer: 'selfwheeling',
    trancheId: 'tranche-c',
    execPrice: '12.10',
    execVolume: '15.0',
    memoFree: '自己託送',
    memoJepx: 'JEPX備考: 自己託送分',
  },
  {
    area: AreaCode.HOKKAIDO,
    timeCode: 21,
    side: Side.BUY,
    price: '12.50',
    volume: '15.0',
    status: BiddingSpotBidStatus.UNMATCHED,
    issuer: 'selfwheeling',
    trancheId: 'tranche-c',
  },

  // 東北 / 北陸 / 中国 / 四国 を散らす
  {
    area: AreaCode.TOHOKU,
    timeCode: 30,
    side: Side.SELL,
    price: '9.50',
    volume: '45.0',
    status: BiddingSpotBidStatus.MATCHED,
    issuer: 'generation',
    trancheId: 'tranche-b',
    execPrice: '9.80',
    execVolume: '45.0',
  },
  {
    area: AreaCode.HOKURIKU,
    timeCode: 32,
    side: Side.BUY,
    price: '10.20',
    volume: '20.0',
    status: BiddingSpotBidStatus.MATCHED,
    issuer: 'retail',
    trancheId: 'tranche-a',
    execPrice: '9.80',
    execVolume: '20.0',
  },
  {
    area: AreaCode.CHUGOKU,
    timeCode: 36,
    side: Side.SELL,
    price: '11.00',
    volume: '50.0',
    status: BiddingSpotBidStatus.PENDING,
    issuer: 'generation',
    trancheId: 'tranche-b',
  },
  {
    area: AreaCode.SHIKOKU,
    timeCode: 38,
    side: Side.BUY,
    price: '13.50',
    volume: '25.0',
    status: BiddingSpotBidStatus.PENDING,
    issuer: 'dr',
    trancheId: 'tranche-c',
  },

  // JEPX 制限違反 (= 価格 999 円超過、 mock)
  {
    area: AreaCode.TOKYO,
    timeCode: 39,
    side: Side.BUY,
    price: '1500.00',
    volume: '10.0',
    status: BiddingSpotBidStatus.PENDING,
    issuer: 'retail',
    trancheId: 'tranche-b',
    memoFree: 'TODO(business-logic): JEPX 上限違反検出テスト用',
  },

  // 量 0.1 未満 違反 (= mock)
  {
    area: AreaCode.TOKYO,
    timeCode: 40,
    side: Side.SELL,
    price: '10.00',
    volume: '0.05',
    status: BiddingSpotBidStatus.PENDING,
    issuer: 'generation',
    trancheId: 'tranche-b',
    memoFree: 'TODO(business-logic): JEPX 最小量違反検出テスト用',
  },

  // 残り 7 件 (= 多様性確保)
  {
    area: AreaCode.TOKYO,
    timeCode: 7,
    side: Side.SELL,
    price: '6.00',
    volume: '50.0',
    status: BiddingSpotBidStatus.MATCHED,
    issuer: 'generation',
    trancheId: 'tranche-a',
    execPrice: '6.50',
    execVolume: '50.0',
  },
  {
    area: AreaCode.TOKYO,
    timeCode: 8,
    side: Side.SELL,
    price: '6.50',
    volume: '50.0',
    status: BiddingSpotBidStatus.MATCHED,
    issuer: 'generation',
    trancheId: 'tranche-a',
    execPrice: '6.50',
    execVolume: '50.0',
  },
  {
    area: AreaCode.CHUBU,
    timeCode: 26,
    side: Side.SELL,
    price: '7.50',
    volume: '90.0',
    status: BiddingSpotBidStatus.MATCHED,
    issuer: 'generation',
    trancheId: 'tranche-a',
    execPrice: '8.00',
    execVolume: '60.0',
  },
  {
    area: AreaCode.KANSAI,
    timeCode: 28,
    side: Side.BUY,
    price: '13.00',
    volume: '80.0',
    status: BiddingSpotBidStatus.MATCHED,
    issuer: 'retail',
    trancheId: 'tranche-b',
    execPrice: '12.80',
    execVolume: '80.0',
  },
  {
    area: AreaCode.KYUSHU,
    timeCode: 33,
    side: Side.BUY,
    price: '11.20',
    volume: '30.0',
    status: BiddingSpotBidStatus.PENDING,
    issuer: 'dr',
    trancheId: 'tranche-c',
  },
  {
    area: AreaCode.TOKYO,
    timeCode: 44,
    side: Side.SELL,
    price: '14.00',
    volume: '20.0',
    status: BiddingSpotBidStatus.PENDING,
    issuer: 'generation',
    trancheId: 'tranche-b',
  },
  {
    area: AreaCode.TOKYO,
    timeCode: 46,
    side: Side.SELL,
    price: '13.50',
    volume: '25.0',
    status: BiddingSpotBidStatus.PENDING,
    issuer: 'generation',
    trancheId: 'tranche-b',
  },
]

/**
 * mock seed を `SpotBidRow[]` に展開する。 id / createdAt は seed index から導出。
 *
 * TODO(rpc): 実装後は GetBiddingSpotApprovalRequest RPC の response.spotBids を
 * 同じ shape に変換する hook (= `convertSpotBidsFromApprovalRequest`) に置換予定。
 */
export const buildSpotBidsSeed = (): SpotBidRow[] =>
  SEEDS.map((s, i) => ({
    id: `mock-bid-${String(i + 1).padStart(3, '0')}`,
    approvalRequestId: baseRequestId,
    areaCode: s.area,
    timeCode: s.timeCode,
    direction: s.side,
    priceYenPerKwh: s.price,
    volumeMwh: s.volume,
    bidStatus: s.status,
    approvalState:
      s.execPrice !== undefined
        ? BiddingApprovalState.SUBMITTED
        : BiddingApprovalState.PENDING,
    issuerType: s.issuer,
    trancheId: s.trancheId,
    execPriceYenPerKwh: s.execPrice,
    execVolumeMwh: s.execVolume,
    memoJepx: s.memoJepx,
    memoFree: s.memoFree,
    createdAt: new Date(baseCreatedAt.getTime() + i * 60 * 1000), // 1 分刻みで作成時刻をずらす
    createdBy: ISSUERS[i % ISSUERS.length],
  }))
